import { ReactElement, ReactNode, useContext } from 'react';

import { Tooltip, ExternalLink, Typography } from '@cast/design-system';

import { NativeHpaMessage } from './NativeHpaMessage';
import { WarningTooltip } from '../../../_components/WarningTooltip';
import { WorkloadDetailsContext } from '../../_context';
import { useHpaDisabledReasons } from '../_hooks/useHpaDisabledReasons';

import { RffControlsProvider } from '@cast/shared-rff-ui';

import { getDocsLink } from '@cast/utils';

type Props = {
  isOnHorizontalPolicy?: boolean;
  annotationsTooltip?: ReactNode;
  readOnlyTooltip?: ReactNode;
  disableTooltip?: boolean;
  followCursor?: boolean;
  children: ReactElement;
  skipAnnotationsTooltip?: boolean;
};

export const ExplanationTooltip = ({
  isOnHorizontalPolicy,
  annotationsTooltip,
  readOnlyTooltip,
  disableTooltip,
  children,
  followCursor,
  skipAnnotationsTooltip = false,
}: Props) => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { isCastware, managedByAnnotations, hasNativeHpa } =
    useHpaDisabledReasons(workload);
  const disableDueToNativeHpa = hasNativeHpa && isOnHorizontalPolicy;

  if (
    (!isCastware && !managedByAnnotations && !disableDueToNativeHpa) ||
    disableTooltip
  ) {
    return <>{children}</>;
  }

  if (disableDueToNativeHpa) {
    return (
      <RffControlsProvider readOnly>
        <Tooltip
          title={<NativeHpaMessage />}
          testId="native-hpa-tooltip"
          followCursor={followCursor}
        >
          {children}
        </Tooltip>
      </RffControlsProvider>
    );
  }

  if (managedByAnnotations) {
    if (skipAnnotationsTooltip) {
      return <>{children}</>;
    }
    return (
      <RffControlsProvider readOnly>
        <WarningTooltip
          title={
            annotationsTooltip || (
              <Typography variant="inherit">
                Workload management is enabled through annotations in the
                definition file.{' '}
                <ExternalLink
                  link={getDocsLink('docs_woop_annotations')}
                  noIcon
                >
                  Learn more
                </ExternalLink>
              </Typography>
            )
          }
          testId="annotations-tooltip"
          followCursor={followCursor}
        >
          {children}
        </WarningTooltip>
      </RffControlsProvider>
    );
  }
  return (
    <RffControlsProvider readOnly>
      <WarningTooltip
        title={
          readOnlyTooltip ||
          'The workload cannot be edited because it is assigned to a castware scaling policy'
        }
        testId="castware-tooltip"
        followCursor={followCursor}
      >
        {children}
      </WarningTooltip>
    </RffControlsProvider>
  );
};
